import { useEffect, useRef } from 'react';
import {
  buildEventMeta,
  resolveEventIdentity,
  trackSurveyEvent,
  type ShellHydration,
} from '../../../api.js';
import type { SurveyQuestion } from '../types.js';

interface UseSurveyTrackingParams {
  campaignId: string | undefined;
  hydration: ShellHydration;
  // Questions currently visible on the page (paged) or current question (conversational).
  // Drives `question_seen` emission — each new key fires once per session.
  visibleQuestions: SurveyQuestion[];
  // Full survey responses map; used to detect started + per-question answered.
  responses: Record<string, unknown>;
  // Total visible questions across the whole survey — used to compute
  // completedRatio when the user dismisses partway through.
  totalVisibleQuestions: number;
}

interface SurveyTrackingControls {
  // Called by the renderer once the network submit returns success.
  emitCompleted: (responseId?: string) => void;
  // Called from the dismiss handler with the last visible question's key.
  emitDismissed: (lastQuestionKey: string | undefined, reason?: string) => void;
  // Called by the validation engine when a rule rejects user input.
  emitValidationFailed: (questionKey: string, rule: string, message: string) => void;
}

// Centralised lifecycle for the analytics event taxonomy described in
// backend/database/models/surveyEvent.ts. The renderer wires its state into
// this hook and gets back imperative emitters for the events that aren't
// triggered by state changes (completed, dismissed, validation_failed).
//
// All events are dedup'd client-side with refs — the backend has a unique
// index too, but client-side dedup avoids the wasted network call.
export function useSurveyTracking({
  campaignId,
  hydration,
  visibleQuestions,
  responses,
  totalVisibleQuestions,
}: UseSurveyTrackingParams): SurveyTrackingControls {
  const seenKeysRef = useRef<Set<string>>(new Set());
  const seenAtRef = useRef<Map<string, number>>(new Map());
  const answeredKeysRef = useRef<Set<string>>(new Set());
  const startedRef = useRef(false);
  const startedAtRef = useRef<number | null>(null);

  // question_seen: emit once per question key on the first frame it becomes
  // visible. The visibleQuestions list shrinks/grows as branching logic
  // toggles questions — re-running this effect each render is cheap because
  // the Set membership check short-circuits anything we've already fired.
  useEffect(() => {
    if (!campaignId) return;
    for (const question of visibleQuestions) {
      if (seenKeysRef.current.has(question.key)) continue;
      seenKeysRef.current.add(question.key);
      const tsStart = perfNow();
      seenAtRef.current.set(question.key, tsStart);
      void trackSurveyEvent(hydration, {
        campaignId,
        eventType: 'question_seen',
        questionKey: question.key,
        ...resolveEventIdentity(hydration),
        meta: { ...buildEventMeta(hydration), tsStart },
      });
    }
  }, [campaignId, hydration, visibleQuestions]);

  // started + answered: started fires once on the first meaningful response.
  // answered fires once per question key when its value first becomes
  // meaningful — duration is the gap between question_seen and now.
  useEffect(() => {
    if (!campaignId) return;
    const meaningfulKeys = Object.keys(responses).filter((key) => isMeaningful(responses[key]));

    if (!startedRef.current && meaningfulKeys.length > 0) {
      startedRef.current = true;
      startedAtRef.current = perfNow();
      void trackSurveyEvent(hydration, {
        campaignId,
        eventType: 'started',
        ...resolveEventIdentity(hydration),
        meta: buildEventMeta(hydration),
      });
    }

    for (const key of meaningfulKeys) {
      if (answeredKeysRef.current.has(key)) continue;
      answeredKeysRef.current.add(key);
      const seenAt = seenAtRef.current.get(key);
      const now = perfNow();
      const duration = seenAt !== undefined ? now - seenAt : undefined;
      void trackSurveyEvent(hydration, {
        campaignId,
        eventType: 'answered',
        questionKey: key,
        ...resolveEventIdentity(hydration),
        meta: {
          ...buildEventMeta(hydration),
          tsStart: seenAt,
          duration,
        },
      });
    }
  }, [campaignId, hydration, responses]);

  return {
    emitCompleted(responseId?: string) {
      if (!campaignId) return;
      const totalDuration = startedAtRef.current !== null ? perfNow() - startedAtRef.current : undefined;
      void trackSurveyEvent(hydration, {
        campaignId,
        eventType: 'completed',
        responseId,
        ...resolveEventIdentity(hydration),
        meta: {
          ...buildEventMeta(hydration),
          duration: totalDuration,
        },
      });
    },
    emitDismissed(lastQuestionKey, reason) {
      if (!campaignId) return;
      const answered = answeredKeysRef.current.size;
      const completedRatio = totalVisibleQuestions > 0 ? answered / totalVisibleQuestions : 0;
      void trackSurveyEvent(hydration, {
        campaignId,
        eventType: 'dismissed',
        questionKey: lastQuestionKey,
        ...resolveEventIdentity(hydration),
        meta: {
          ...buildEventMeta(hydration),
          lastQuestionKey,
          completedRatio,
          reason,
        },
      });
    },
    emitValidationFailed(questionKey, rule, message) {
      if (!campaignId) return;
      void trackSurveyEvent(hydration, {
        campaignId,
        eventType: 'validation_failed',
        questionKey,
        ...resolveEventIdentity(hydration),
        meta: {
          ...buildEventMeta(hydration),
          rule,
          message,
        },
      });
    },
  };
}

function perfNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function isMeaningful(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string' && value.trim().length === 0) return false;
  if (Array.isArray(value) && value.length === 0) return false;
  if (typeof value === 'object' && Object.keys(value as object).length === 0) return false;
  return true;
}
